import { ListItemText, Popover, Stack, Typography, useTheme } from '@mui/material'
import { StyledSelectTextFieldWithDarkMode, MenuItemWithDarkMode } from 'dark-mode-components'
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { useState } from 'react'
import { SourceDocument, TwinNoteSummary } from 'api/model'
import AttachDocumentsPopover from './AttachDocumentsPopover'
import AttachNotesPopover from './AttachNotesPopover'
import { COLORS } from 'app-constants'

export enum DocumentType {
  Documents = 'Documents',
  Notes = 'Notes',
}

// this component is responsible for indicating what type of document we are trying to attach
const AttachDocumentSelector = ({
  documentType,
  setDocumentType,
  notes,
  setNotes,
  sourceDocuments,
  setSourceDocuments,
}: {
  documentType: DocumentType | null
  setDocumentType: React.Dispatch<React.SetStateAction<DocumentType | null>>
  notes: TwinNoteSummary[]
  setNotes: React.Dispatch<React.SetStateAction<TwinNoteSummary[]>>
  setSourceDocuments: React.Dispatch<React.SetStateAction<SourceDocument[]>>
  sourceDocuments: SourceDocument[]
}) => {
  const theme = useTheme()

  // logic for the AttachDocumentsPopover / AttachNotesPopover components
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleClose = () => {
    setDocumentType(null)
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  return (
    <Stack mt={1}>
      <Typography
        variant="h7.5"
        sx={{
          color: COLORS.GRAY_1,
        }}>
        Attach relevant files
      </Typography>
      <StyledSelectTextFieldWithDarkMode
        className="attach-document-selector"
        defaultValue=""
        id="attach-document-selector"
        isDarkBackdrop={false}
        placeholder="Attach"
        theme={theme}
        value={documentType || ''}
        onChange={(e: any) => {
          setDocumentType(e?.target.value as DocumentType)
          const buttonEl = document.querySelector('.attach-document-selector')
          if (buttonEl) {
            setAnchorEl(buttonEl as HTMLButtonElement)
          }
        }}
        sx={{
          width: '160px',
          mt: 1,
        }}>
        <MenuItemWithDarkMode theme={theme} value={'Documents'} isSelected={documentType === DocumentType.Documents}>
          <DescriptionOutlinedIcon
            sx={{
              height: '24px',
              width: '24px',
              fill: COLORS.GRAY_2,
            }}
          />

          <ListItemText
            primary={`Documents`}
            sx={{
              color: COLORS.GRAY_0,
              ml: 1,
            }}
          />
        </MenuItemWithDarkMode>
        <MenuItemWithDarkMode theme={theme} value={'Notes'} isSelected={documentType === DocumentType.Notes}>
          <TextSnippetOutlinedIcon
            sx={{
              height: '24px',
              width: '24px',
              fill: COLORS.GRAY_2,
            }}
          />
          <ListItemText
            primary={'Notes'}
            sx={{
              color: COLORS.GRAY_0,
              ml: 1,
            }}
          />
        </MenuItemWithDarkMode>
      </StyledSelectTextFieldWithDarkMode>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}>
        {documentType === DocumentType.Documents && (
          <AttachDocumentsPopover
            onClose={handleClose}
            existingDocuments={sourceDocuments}
            onAddDocuments={setSourceDocuments}
          />
        )}
        {documentType === DocumentType.Notes && (
          <AttachNotesPopover onClose={handleClose} existingNotes={notes} onAddNotes={setNotes} />
        )}
      </Popover>
    </Stack>
  )
}

export default AttachDocumentSelector
